<template>
    <div>
        <template v-if="loading">
            <loading-spinner/>
        </template>
        <template v-if="!loading">
            <div>
                <data-list v-model="summaryData"/>
            </div>
        </template>
    </div>
</template>

<script>

import DataList from '~/components/Shared/UIComponents/DataList';
import productConfig from '~/mixins/productConfig';

export default {
    name: 'StandalonePolicySummaryDataList',
    components: {
        DataList
    },
    mixins: [productConfig],
    props: {
        value: Object,
        multipleEnitities: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            loading: false,
            skipProductProperties: ['prior_experience'],
        }
    },
    computed: {
        risk() {
            return this.value;
        },
        summaryData() {
            const summaryDataList = [];

            summaryDataList.push(...this.insuredFields);

            // entity data
            if (!this.multipleEnitities) {
                summaryDataList.push(...this.entityFields);
            }

            // broker data
            summaryDataList.push(...this.brokerData);

            // date data
            if (this.isDeclaration) {
                summaryDataList.push(...this.dateData);
            }

            // general data
            summaryDataList.push(...this.generalData);

            //product specific data
            summaryDataList.push(...this.productDataFields);

            if (this.productHasProperty('gross_margin') && this.value.gross_margin) {
                summaryDataList.push(
                    {
                        text: this.getPropertyLabel('gross_margin'),
                        value: this.$options.filters.numberNoTrail(this.value.gross_margin) + ' bps'
                    },
                );
            }

            if (this.productHasProperty('net_margin') && this.value.net_margin) {
                summaryDataList.push(
                    {
                        text: this.getPropertyLabel('net_margin'),
                        value: this.$options.filters.numberNoTrail(this.value.net_margin) + ' bps'
                    },
                );
            }

            if (this.value.sub_sector_id !== null) {
                summaryDataList.push(
                    {
                        text: 'Sector',
                        value: this.value.sub_sector.sector.name
                    },
                    {
                        text: 'Sub sector',
                        value: this.value.custom_sub_sector ? this.value.custom_sub_sector : this.value.sub_sector.name
                    }
                );
            }

            if (this.isDeclaration) {
                summaryDataList.push(
                    {
                        text: this.getPropertyLabel('waiting_period'),
                        value: `${this.value.waiting_period} days`
                    }
                );
            } else if (this.value.hasOwnProperty('requested_waiting_period')) {
                summaryDataList.push(
                    {
                        text: this.getPropertyLabel('requested_waiting_period'),
                        value: `${this.value.requested_waiting_period} days`
                    }
                );
            }

            // placing broker contact data
            summaryDataList.push(...this.placingBrokerContactData)

            return summaryDataList;
        },
        isDeclaration() {
            return this.value.risk_type === 'declaration';
        },
        insuredFields() {
            const submissionInsured = this.value.client_company;

            return [
                {
                    text: 'Insured',
                    value: submissionInsured.name
                },
                {
                    text: 'Insured address',
                    value: submissionInsured.address?.line1
                }
            ];
        },
        entityFields() {
            if (!this.value.entities) {
                return [];
            }

            const submissionEntity = this.value.entities[0];

            return [
                {
                    text: 'Entity',
                    value: submissionEntity.entity.name
                },
                {
                    text: 'Entity address',
                    value: submissionEntity.address?.line1
                }
            ];
        },
        dateData() {
            let dateData = [];

            if (this.value.inception_date) {
                dateData.push(
                    {
                        text: 'Start date',
                        value: this.$options.filters.formatDate(this.value.inception_date)
                    }
                );
            }

            if (this.value.expiry_date) {
                dateData.push(
                    {
                        text: 'End date',
                        value: this.$options.filters.formatDate(this.value.off_risk_at ?? this.value.expiry_date)
                    }
                );
            }

            if (this.value.created_at) {
                dateData.push(
                    {
                        text: 'Bound date',
                        value: this.$options.filters.formatDate(this.value.created_at)
                    }
                );
            }

            if (this.value.updated_at) {
                dateData.push(
                    {
                        text: 'Updated date',
                        value: this.$options.filters.formatDate(this.value.updated_at)
                    }
                );
            }

            return dateData;
        },
        generalData() {
            let generalData = [
                {
                    text: 'Total limit of liability',
                    value: this.formatCurrency(this.value.limit_of_liability)
                },
                {
                    text: 'Choice of law and Jurisdiction',
                    value: this.value?.choice_of_law?.name || ''
                },
                {
                    text: 'Interest',
                    value: this.value.interest
                },
                {
                    text: 'Taxes payable by Insured & administered by Insurer(s)',
                    value: this.value.taxes_payable_by_insured + '%'
                },
                {
                    text: 'Taxes payable and administered by Insurer(s)',
                    value: this.value.taxes_payable_by_insurer + '%'
                },
            ];

            if (this.value.taxes_payable_by_insurer_and_administered_by_the_insured) {
                generalData.push(
                    {
                        text: 'Taxes payable by Insurer(s) and administered by the Insured or their agent',
                        value: this.value.taxes_payable_by_insurer_and_administered_by_the_insured + '%'
                    }
                );
            }

            if (this.value.taxes_payable_and_administered_by_the_insured) {
                generalData.push(
                    {
                        text: 'Taxes payable and administered by the Insured or their agent',
                        value: this.value.taxes_payable_and_administered_by_the_insured + '%'
                    }
                );
            }

            generalData.push(...[
                {
                    text: 'Regulatory risk location',
                    value: this.value.regulatory_risk_location?.name
                },
                {
                    text: 'US Classification',
                    value: this.value.us_classification
                },
                {
                    text: 'Allocation of premium to coding', // TODO: Ask OBI, can be multiple
                    value: this.allocationOfPremiumToCoding
                }
            ]);

            if (this.hasAllocationOfPremiumToCoding) {
                generalData.push(...this.allocationOfPremiumToCodingValues);
            }

            if (this.value.adjustment_premium_period_of_credit) {
                generalData.push(
                    {
                        text: 'Adjustment Premium Period of Credit',
                        value: this.value.adjustment_premium_period_of_credit
                    }
                );
            }

            if (this.value.overseas_broker_address) {
                generalData.push(
                    {
                        text: 'Overseas Broker Address',
                        value: this.value.overseas_broker_address
                    }
                );
            }

            generalData.push(...[
                {
                    text: 'Regulatory Client classification',
                    value: this.value.regulatory_client_classification
                },
                {
                    text: 'Policy currency',
                    value: this.value.currency.code
                },
                {
                    text: 'Category',
                    value: this.value.category.name
                },
                {
                    text: 'Product',
                    value: this.value.product.name
                }
            ]);

            if (this.productHasProperty('total_contract_value')) {
                this.skipProductProperties.push('total_contract_value');
                generalData.push(
                    {
                        text: this.getPropertyLabel('total_contract_value'),
                        value: this.applyFormatFilter('total_contract_value', 'currency'),
                    },
                );
            }

            generalData.push(
                {
                    text: this.getPropertyLabel('contract_value'),
                    value: this.value.current_contract_value ? this.formatCurrency(this.value.current_contract_value) : this.formatCurrency(this.value.contract_value)
                },
            );

            if (this.productHasProperty('indemnity')) {
                generalData.push({
                    text: this.getPropertyLabel('indemnity'),
                    value: this.$options.filters.percent(this.value.indemnity)
                })
            }

            let totalRiskPeriod;

            if (this.isFirmOrder || this.isDeclaration) {
                const risk = this.isFirmOrder ? this.firmOrder : this.value;
                const startDate = this.$moment(risk.inception_date);
                // if off risk then use the off risk date
                const endDate = this.isOffRisk ? this.$moment(risk.off_risk_at) : this.$moment(risk.expiry_date);

                totalRiskPeriod = endDate.diff(startDate, 'days');
            } else {
                totalRiskPeriod = this.value.total_risk_period;
            }

            generalData.push(
                {
                    text: this.getPropertyLabel('total_risk_period'),
                    value: this.$options.filters.yearsAndDays(totalRiskPeriod)
                }
            );

            return generalData;
        },
        placingBrokerContactData() {
            return [
                {
                    text: 'Placing broker contact',
                    value: this.value.placing_broker_contact?.name
                },
            ]
        },
        brokerData() {
            return [
                {
                    text: 'Broker',
                    value: this.value.producing_broker_company.name
                },
                {
                    text: 'Broker address',
                    value: this.value.broker_address?.line1
                },
                {
                    text: 'Location',
                    value: this.value.broker_address?.country?.name
                }
            ];
        },
        productDataFields() {
            let productData = [];

            this.productQuestions.forEach(question => {
                if (!this.isProductDataProperty(question) || this.isSkippedProperty(question.field)) {
                    return;
                }

                const productDataField = question;

                if (productDataField.hasOwnProperty('type') && !this.isTypeRiskPeriod(productDataField.type)) {
                    const value = this.applyFormatFilter(productDataField.field, productDataField.type);
                    const suffix = productDataField.hasOwnProperty('suffix') ? productDataField.suffix : '';

                    productData.push({
                        text: this.getPropertyLabel(productDataField.field),
                        value: `${value}${suffix}`
                    });
                }
            });

            this.productQuestions.forEach(question => {
                if (!this.isProductDataProperty(question)) {
                    return false;
                }

                if (question.hasOwnProperty('type') && this.isTypeRiskPeriod(question.type) && this.value.product_data[question.field]) {
                    productData.push({
                        text: this.getPropertyLabel(question.field),
                        value: this.applyFormatFilter(question.field, question.type)
                    });
                }
            });

            return productData;
        },
        hasAllocationOfPremiumToCoding() {
            return this.value.risk_codes && this.value.risk_codes.length > 0;
        },
        allocationOfPremiumToCoding() {
            if (!this.hasAllocationOfPremiumToCoding) {
                return '';
            }

            return this.value.risk_codes.map((submissionRiskCode) => {
                return submissionRiskCode.risk_code.name;
            }).join(', ');
        },
        allocationOfPremiumToCodingValues() {
            if (!this.hasAllocationOfPremiumToCoding) {
                return null;
            }

            return this.value.risk_codes.map((submissionRiskCode) => {
                return {
                    text: submissionRiskCode.risk_code.name.concat(' Allocation'),
                    value: this.formatPercentage(submissionRiskCode.value)
                }
            });
        }
    },
    methods: {
        formatCurrency(value) {
            return this.$options.filters.currency(value, this.value.currency.code);
        },
        formatPercentage(value) {
            return this.$options.filters.percent(value);
        },
        applyFormatFilter(key, format) {
            switch (format) {
                case 'currency':
                    return this.formatCurrency(this.value.product_data[key]);
                case 'days':
                case 'risk_period':
                    return this.$options.filters.yearsAndDays(this.value.product_data[key]);
                case 'yes_no':
                case 'boolean':
                    return this.value.product_data[key] ? 'Yes' : 'No';
                default:
                    return this.value.product_data[key] || '';
            }
        },
        isSkippedProperty(property) {
            return this.skipProductProperties.includes(property);
        },
    }
}
</script>
